import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Card, Button, Row, Col, Typography, Space, Spin, message, Form, Input,
  Switch, Table, Modal, Tag, Tabs, Popconfirm, InputNumber, DatePicker,
  Empty, Divider, Statistic, Upload, Image,
} from 'antd';
import {
  ArrowLeftOutlined, PlusOutlined, DeleteOutlined, EditOutlined,
  GlobalOutlined, ShoppingOutlined, CalendarOutlined, PhoneOutlined,
  EyeOutlined, CopyOutlined, StarOutlined, CheckCircleOutlined, StopOutlined, TranslationOutlined,
  UploadOutlined, MenuOutlined,
} from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { scenarioSiteAdminAPI, productsAPI, scenarioAPI } from '../services/api';
import AlayaessAgentPicker, { AlayaessAgentPickerHint } from '../components/AlayaessAgentPicker';
import dayjs from 'dayjs';

const { Title, Text, Paragraph } = Typography;
const { TextArea } = Input;

export default function ScenarioSiteAdminPage() {
  const { t, i18n } = useTranslation();
  const { id: scenarioId } = useParams();
  const navigate = useNavigate();
  const [scenario, setScenario] = useState(null);
  const [siteConfig, setSiteConfig] = useState(null);
  const [siteProducts, setSiteProducts] = useState([]);
  const [events, setEvents] = useState([]);
  const [inquiries, setInquiries] = useState([]);
  const [allProducts, setAllProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [configForm] = Form.useForm();
  const [showAddProduct, setShowAddProduct] = useState(false);
  const [showEventModal, setShowEventModal] = useState(null);
  const [eventForm] = Form.useForm();
  const [regModal, setRegModal] = useState(null);
  const [registrations, setRegistrations] = useState([]);
  const [siteSections, setSiteSections] = useState([]);
  const [autoGenLoading, setAutoGenLoading] = useState(false);
  const [translatingId, setTranslatingId] = useState(null);
  const [productModal, setProductModal] = useState(null); // null | 'new' | sp object
  const [productForm] = Form.useForm();
  const [navTabs, setNavTabs] = useState([]);
  const [stickyCta, setStickyCta] = useState(true);
  const [showLogin, setShowLogin] = useState(true);
  const [editSection, setEditSection] = useState(null);
  const [sectionForm] = Form.useForm();

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [scRes, prodRes] = await Promise.all([
        scenarioAPI.get(scenarioId),
        productsAPI.getAll({ limit: 500 }),
      ]);
      setScenario(scRes.data);
      setAllProducts(prodRes.data?.products || prodRes.data || []);

      try {
        const cfgRes = await scenarioSiteAdminAPI.getSiteConfig(scenarioId);
        setSiteConfig(cfgRes.data);
        configForm.setFieldsValue(cfgRes.data);
        // Init nav tabs from config
        const nc = cfgRes.data?.nav_config;
        const defTabs = [
          { key: 'home', order: 0 }, { key: 'products', order: 1 },
          { key: 'events', order: 2 }, { key: 'blog', order: 3 },
          { key: 'survey', order: 4 }, { key: 'faq', order: 5 },
          { key: 'testimonial', order: 6 }, { key: 'contact', order: 7 },
        ];
        const saved = nc?.tabs;
        if (saved && typeof saved === 'object') {
          const tabs = defTabs.map(d => {
            const s = saved[d.key] || {};
            return { key: d.key, label: s.label || '', label_th: s.label_th || '', visible: s.visible !== false, order: s.order ?? d.order };
          });
          tabs.sort((a, b) => a.order - b.order);
          setNavTabs(tabs);
        } else {
          setNavTabs(defTabs.map(d => ({ ...d, label: '', label_th: '', visible: true })));
        }
        setStickyCta(nc?.sticky_cta !== false);
        setShowLogin(nc?.login?.visible !== false);
      } catch {
        setSiteConfig(null);
      }

      try {
        const spRes = await scenarioSiteAdminAPI.listProducts(scenarioId);
        setSiteProducts(spRes.data || []);
      } catch { /* ignore */ }

      try {
        const evtRes = await scenarioSiteAdminAPI.listEvents(scenarioId);
        setEvents(evtRes.data || []);
      } catch { /* ignore */ }

      try {
        const inqRes = await scenarioSiteAdminAPI.listInquiries(scenarioId);
        setInquiries(inqRes.data || []);
      } catch { /* ignore */ }

      try {
        const secRes = await scenarioSiteAdminAPI.listSections(scenarioId);
        setSiteSections(secRes.data || []);
      } catch { /* ignore */ }
    } catch (e) {
      message.error(t('siteAdmin.loadFailed'));
    }
    setLoading(false);
  }, [scenarioId, configForm]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const handleUploadFile = async (file, category = 'site') => {
    try {
      const res = await scenarioSiteAdminAPI.uploadFile(scenarioId, file, category);
      message.success(t('siteAdmin.config.uploadSuccess'));
      return res.data.url;
    } catch (e) {
      message.error(e.message || t('siteAdmin.config.uploadFailed'));
      return null;
    }
  };

  const handleSaveNavConfig = async () => {
    setSaving(true);
    try {
      const tabsObj = {};
      navTabs.forEach((tab, idx) => {
        tabsObj[tab.key] = {
          label: tab.label || '',
          label_th: tab.label_th || '',
          visible: tab.visible,
          order: idx,
        };
      });
      const navConfig = {
        sticky_cta: stickyCta,
        login: { visible: showLogin },
        tabs: tabsObj,
      };
      await scenarioSiteAdminAPI.updateSiteConfig(scenarioId, { nav_config: navConfig });
      message.success(t('siteAdmin.config.navSaved'));
    } catch (e) {
      message.error(e.message || t('siteAdmin.saveFailed'));
    }
    setSaving(false);
  };

  const handleMoveNavTab = (idx, direction) => {
    const newIdx = idx + direction;
    if (newIdx < 0 || newIdx >= navTabs.length) return;
    const newTabs = [...navTabs];
    [newTabs[idx], newTabs[newIdx]] = [newTabs[newIdx], newTabs[idx]];
    setNavTabs(newTabs);
  };

  const handleNavTabChange = (idx, field, value) => {
    setNavTabs(prev => prev.map((tab, i) => i === idx ? { ...tab, [field]: value } : tab));
  };

  // ── Site Config ──────────────────────────────────────
  const handleCreateConfig = async () => {
    if (!scenario) return;
    setSaving(true);
    try {
      const slug = scenario.name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '') || `site-${scenarioId}`;
      const res = await scenarioSiteAdminAPI.createSiteConfig(scenarioId, {
        slug,
        brand_name: scenario.name,
        hero_title: scenario.name,
        hero_subtitle: scenario.description || '',
        is_published: false,
      });
      setSiteConfig(res.data);
      configForm.setFieldsValue(res.data);
      message.success(t('siteAdmin.siteCreated'));
    } catch (e) {
      message.error(e.message || t('siteAdmin.createFailed'));
    }
    setSaving(false);
  };

  const handleSaveConfig = async () => {
    setSaving(true);
    try {
      const values = configForm.getFieldsValue();
      const res = await scenarioSiteAdminAPI.updateSiteConfig(scenarioId, values);
      setSiteConfig(res.data);
      message.success(t('siteAdmin.saved'));
    } catch (e) {
      message.error(e.message || t('siteAdmin.saveFailed'));
    }
    setSaving(false);
  };

  // ── Products ─────────────────────────────────────────
  const addedProductIds = new Set(siteProducts.map(sp => sp.product_id));
  const availableProducts = allProducts.filter(p => !addedProductIds.has(p.id) && p.is_active);

  const handleAddProduct = async (productId) => {
    try {
      await scenarioSiteAdminAPI.addProduct(scenarioId, {
        product_id: productId,
        display_order: siteProducts.length,
      });
      const res = await scenarioSiteAdminAPI.listProducts(scenarioId);
      setSiteProducts(res.data || []);
      message.success(t('siteAdmin.productsMgmt.addedProduct'));
    } catch (e) {
      message.error(e.message || t('siteAdmin.productsMgmt.addFailed'));
    }
  };

  const handleRemoveProduct = async (spId) => {
    try {
      await scenarioSiteAdminAPI.removeProduct(scenarioId, spId);
      setSiteProducts(prev => prev.filter(sp => sp.id !== spId));
      message.success(t('siteAdmin.productsMgmt.removed'));
    } catch (e) {
      message.error(e.message || t('siteAdmin.productsMgmt.removeFailed'));
    }
  };

  const handleToggleFeatured = async (sp) => {
    try {
      await scenarioSiteAdminAPI.updateProduct(scenarioId, sp.id, { is_featured: !sp.is_featured });
      const res = await scenarioSiteAdminAPI.listProducts(scenarioId);
      setSiteProducts(res.data || []);
    } catch { /* ignore */ }
  };

  const handleOpenProductModal = (sp) => {
    if (sp === 'new') {
      setProductModal('new');
      productForm.resetFields();
    } else {
      setProductModal(sp);
      productForm.setFieldsValue({
        name: sp.product?.name || '',
        short_description: sp.product?.short_description || '',
        description: sp.product?.description || '',
        organization: sp.product?.organization || '',
        price: sp.product?.price,
        currency: sp.product?.currency || 'TWD',
        thumbnail: sp.product?.thumbnail || '',
        features: sp.product?.features ? sp.product.features.join('\n') : '',
        tags: sp.product?.tags ? sp.product.tags.join(', ') : '',
        custom_price_note: sp.custom_price_note || '',
        custom_description: sp.custom_description || '',
        is_featured: sp.is_featured || false,
      });
    }
  };

  const handleSaveProduct = async (values) => {
    setSaving(true);
    try {
      const payload = {
        ...values,
        features: values.features ? values.features.split('\n').map(s => s.trim()).filter(Boolean) : null,
        tags: values.tags ? values.tags.split(',').map(s => s.trim()).filter(Boolean) : null,
        price: values.price || null,
      };
      if (productModal === 'new') {
        await scenarioSiteAdminAPI.createProductDirect(scenarioId, payload);
        message.success(t('siteAdmin.productsMgmt.productCreated'));
      } else {
        await scenarioSiteAdminAPI.updateProductDirect(scenarioId, productModal.id, payload);
        message.success(t('siteAdmin.productsMgmt.productUpdated'));
      }
      const res = await scenarioSiteAdminAPI.listProducts(scenarioId);
      setSiteProducts(res.data || []);
      setProductModal(null);
      productForm.resetFields();
    } catch (e) {
      message.error(e.message || t('siteAdmin.saveFailed'));
    }
    setSaving(false);
  };

  const handleMoveProduct = async (spId, direction) => {
    const sorted = [...siteProducts].sort((a, b) => a.display_order - b.display_order);
    const idx = sorted.findIndex(s => s.id === spId);
    if (idx < 0) return;
    const newIdx = idx + direction;
    if (newIdx < 0 || newIdx >= sorted.length) return;
    const swapped = [...sorted];
    [swapped[idx], swapped[newIdx]] = [swapped[newIdx], swapped[idx]];
    const reorder = swapped.map((s, i) => ({ id: s.id, section_order: i }));
    try {
      await scenarioSiteAdminAPI.reorderProducts(scenarioId, { sections: reorder });
      setSiteProducts(swapped.map((s, i) => ({ ...s, display_order: i })));
    } catch (e) {
      message.error(e.message || t('siteAdmin.sectionsMgmt.sortFailed'));
    }
  };

  // ── Events ───────────────────────────────────────────
  const handleSaveEvent = async (values) => {
    setSaving(true);
    try {
      const dateFields = ['event_date', 'visible_from', 'visible_until', 'registration_deadline'];
      const payload = { ...values };
      dateFields.forEach(f => {
        payload[f] = values[f] ? values[f].toISOString() : null;
      });
      if (showEventModal === 'new') {
        await scenarioSiteAdminAPI.createEvent(scenarioId, payload);
      } else {
        await scenarioSiteAdminAPI.updateEvent(scenarioId, showEventModal.id, payload);
      }
      const res = await scenarioSiteAdminAPI.listEvents(scenarioId);
      setEvents(res.data || []);
      setShowEventModal(null);
      eventForm.resetFields();
      message.success(t('siteAdmin.eventsMgmt.saved'));
    } catch (e) {
      message.error(e.message || t('siteAdmin.saveFailed'));
    }
    setSaving(false);
  };

  const handleToggleEventActive = async (evt) => {
    try {
      await scenarioSiteAdminAPI.updateEvent(scenarioId, evt.id, { is_active: !evt.is_active });
      setEvents(prev => prev.map(e => e.id === evt.id ? { ...e, is_active: !e.is_active } : e));
    } catch (e) {
      message.error(e.message || t('siteAdmin.operationFailed'));
    }
  };

  const getEventStatus = (evt) => {
    if (!evt.is_active) return { color: 'default', text: t('siteAdmin.eventsMgmt.statusDisabled') };
    const now = new Date();
    if (evt.visible_from && now < new Date(evt.visible_from)) return { color: 'orange', text: t('siteAdmin.eventsMgmt.statusNotOpen') };
    if (evt.visible_until && now > new Date(evt.visible_until)) return { color: 'default', text: t('siteAdmin.eventsMgmt.statusEnded') };
    if (evt.registration_deadline && now > new Date(evt.registration_deadline)) return { color: 'red', text: t('siteAdmin.eventsMgmt.statusDeadline') };
    return { color: 'green', text: t('siteAdmin.eventsMgmt.statusOpen') };
  };

  const handleDeleteEvent = async (eventId) => {
    try {
      await scenarioSiteAdminAPI.deleteEvent(scenarioId, eventId);
      setEvents(prev => prev.filter(e => e.id !== eventId));
      message.success(t('siteAdmin.eventsMgmt.deleted'));
    } catch (e) {
      message.error(e.message || t('siteAdmin.eventsMgmt.deleteFailed'));
    }
  };

  const handleViewRegs = async (event) => {
    setRegModal(event);
    try {
      const res = await scenarioSiteAdminAPI.listRegistrations(scenarioId, event.id);
      setRegistrations(res.data || []);
    } catch { setRegistrations([]); }
  };

  // ── Site Sections ────────────────────────────────────
  const handleAutoGenerate = async () => {
    setAutoGenLoading(true);
    try {
      const res = await scenarioSiteAdminAPI.autoGenerateSite(scenarioId);
      message.success(t('siteAdmin.sectionsMgmt.autoGenerated', { count: res.data?.sections_count || 0 }));
      await fetchAll();
    } catch (e) {
      message.error(e.message || t('siteAdmin.sectionsMgmt.autoGenFailed'));
    }
    setAutoGenLoading(false);
  };

  const handleToggleSectionVisible = async (section) => {
    try {
      await scenarioSiteAdminAPI.updateSection(scenarioId, section.id, { is_visible: !section.is_visible });
      setSiteSections(prev => prev.map(s => s.id === section.id ? { ...s, is_visible: !s.is_visible } : s));
    } catch (e) {
      message.error(e.message || t('siteAdmin.operationFailed'));
    }
  };

  const handleDeleteSection = async (sectionId) => {
    try {
      await scenarioSiteAdminAPI.deleteSection(scenarioId, sectionId);
      setSiteSections(prev => prev.filter(s => s.id !== sectionId));
      message.success(t('siteAdmin.sectionsMgmt.deleted'));
    } catch (e) {
      message.error(e.message || t('siteAdmin.operationFailed'));
    }
  };

  const handleTranslateContent = async (contentId, targetLang) => {
    setTranslatingId(contentId);
    try {
      await scenarioSiteAdminAPI.translateContent(scenarioId, contentId, { language: targetLang });
      message.success(t('siteAdmin.sectionsMgmt.translateSuccess', { lang: targetLang }));
    } catch (e) {
      message.error(e.message || t('siteAdmin.sectionsMgmt.translateFailed'));
    } finally {
      setTranslatingId(null);
    }
  };

  const handleMoveSection = async (sectionId, direction) => {
    const sorted = [...siteSections].sort((a, b) => a.section_order - b.section_order);
    const idx = sorted.findIndex(s => s.id === sectionId);
    if (idx < 0) return;
    const newIdx = idx + direction;
    if (newIdx < 0 || newIdx >= sorted.length) return;
    const newSections = [...sorted];
    [newSections[idx], newSections[newIdx]] = [newSections[newIdx], newSections[idx]];
    const reorder = newSections.map((s, i) => ({ id: s.id, section_order: i }));
    try {
      await scenarioSiteAdminAPI.reorderSections(scenarioId, { sections: reorder });
      setSiteSections(newSections.map((s, i) => ({ ...s, section_order: i })));
    } catch (e) {
      message.error(e.message || t('siteAdmin.sectionsMgmt.sortFailed'));
    }
  };

  const handleUpdateSectionTime = async (sectionId, field, value) => {
    try {
      await scenarioSiteAdminAPI.updateSection(scenarioId, sectionId, {
        [field]: value ? value.toISOString() : null,
      });
      setSiteSections(prev => prev.map(s => s.id === sectionId ? { ...s, [field]: value ? value.toISOString() : null } : s));
      message.success(t('siteAdmin.sectionsMgmt.updated'));
    } catch (e) {
      message.error(e.message || t('siteAdmin.operationFailed'));
    }
  };

  const handleOpenSectionEdit = (sec) => {
    setEditSection(sec);
    sectionForm.setFieldsValue({
      title: sec.title || '',
      subtitle: sec.subtitle || '',
      title_th: sec.config?.title_th || '',
      subtitle_th: sec.config?.subtitle_th || '',
    });
  };

  const handleSaveSectionEdit = async (values) => {
    if (!editSection) return;
    setSaving(true);
    try {
      const payload = {
        title: values.title || null,
        subtitle: values.subtitle || null,
        config: {
          ...(editSection.config || {}),
          title_th: values.title_th || '',
          subtitle_th: values.subtitle_th || '',
        },
      };
      await scenarioSiteAdminAPI.updateSection(scenarioId, editSection.id, payload);
      setSiteSections(prev => prev.map(s =>
        s.id === editSection.id ? { ...s, title: payload.title, subtitle: payload.subtitle, config: payload.config } : s
      ));
      setEditSection(null);
      sectionForm.resetFields();
      message.success(t('siteAdmin.sectionsMgmt.updated'));
    } catch (e) {
      message.error(e.message || t('siteAdmin.operationFailed'));
    }
    setSaving(false);
  };

  const SECTION_TYPE_EMOJIS = {
    hero: '🏠', presentation: '📊', product_grid: '🛒', event_list: '📅',
    cta_banner: '🎯', survey: '📋', blog: '📰', testimonial: '💬',
    inquiry_form: '📩', faq: '❓', ai_content: '🤖', custom_html: '📝',
  };
  const getSectionLabel = (type) => `${SECTION_TYPE_EMOJIS[type] || ''} ${t(`siteAdmin.sectionsMgmt.sectionTypes.${type}`) || type}`;

  const handleQuickPublish = async (publish) => {
    setSaving(true);
    try {
      const values = configForm.getFieldsValue();
      await scenarioSiteAdminAPI.updateSiteConfig(scenarioId, { ...values, is_published: publish });
      const res = await scenarioSiteAdminAPI.getSiteConfig(scenarioId);
      setSiteConfig(res.data);
      configForm.setFieldsValue(res.data);
      message.success(publish ? t('siteAdmin.publishedMsg') : t('siteAdmin.unpublishedMsg'));
    } catch (e) {
      message.error(e.message || t('siteAdmin.operationFailed'));
    }
    setSaving(false);
  };

  if (loading) {
    return <div style={{ textAlign: 'center', padding: 80 }}><Spin size="large" /></div>;
  }

  const publicUrl = siteConfig ? `/s/${siteConfig.slug}` : null;

  return (
    <div style={{ maxWidth: 1200, margin: '0 auto' }}>
      <Space style={{ marginBottom: 16 }}>
        <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/scenarios')}>{t('siteAdmin.backToScenarios')}</Button>
        <Button icon={<ArrowLeftOutlined />} onClick={() => navigate(`/scenarios/${scenarioId}`)}>{t('siteAdmin.scenarioEditor')}</Button>
      </Space>

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24, flexWrap: 'wrap', gap: 12 }}>
        <Title level={3} style={{ margin: 0 }}>
          <GlobalOutlined style={{ marginRight: 8 }} />
          {t('siteAdmin.pageTitle')} — {scenario?.name}
        </Title>
        {siteConfig && (
          <Space wrap>
            <Tag color={siteConfig.is_published ? 'green' : 'default'} style={{ fontSize: 14, padding: '4px 12px' }}>
              {siteConfig.is_published ? t('siteAdmin.online') : t('siteAdmin.draft')}
            </Tag>
            {siteConfig.is_published ? (
              <Button
                danger
                icon={<StopOutlined />}
                onClick={() => handleQuickPublish(false)}
                loading={saving}
              >
                {t('siteAdmin.offline')}
              </Button>
            ) : (
              <Button
                type="primary"
                icon={<CheckCircleOutlined />}
                onClick={() => handleQuickPublish(true)}
                loading={saving}
                style={{ background: '#52c41a', borderColor: '#52c41a' }}
              >
                {t('siteAdmin.publish')}
              </Button>
            )}
            {publicUrl && (
              <>
                <a href={publicUrl} target="_blank" rel="noopener noreferrer">
                  <Button icon={<EyeOutlined />}>
                    {t('siteAdmin.openSite')}
                  </Button>
                </a>
                <Button
                  icon={<CopyOutlined />}
                  onClick={() => {
                    navigator.clipboard.writeText(window.location.origin + publicUrl);
                    message.success(t('siteAdmin.linkCopied'));
                  }}
                >
                  {t('siteAdmin.copyLink')}
                </Button>
              </>
            )}
          </Space>
        )}
      </div>

      {!siteConfig ? (
        <Card style={{ textAlign: 'center', padding: 40 }}>
          <Space direction="vertical" size={16}>
            <GlobalOutlined style={{ fontSize: 48, color: '#ccc' }} />
            <Title level={4}>{t('siteAdmin.noSite')}</Title>
            <Paragraph type="secondary">{t('siteAdmin.noSiteDesc')}</Paragraph>
            <Button type="primary" size="large" icon={<PlusOutlined />} onClick={handleCreateConfig} loading={saving}>
              {t('siteAdmin.createSite')}
            </Button>
          </Space>
        </Card>
      ) : (
        <Tabs
          defaultActiveKey="config"
          items={[
            {
              key: 'config',
              label: <span><GlobalOutlined /> {t('siteAdmin.tabConfig')}</span>,
              children: (
                <Card>
                  <Form form={configForm} layout="vertical" onFinish={handleSaveConfig}>
                    <Row gutter={24}>
                      <Col xs={24} md={12}>
                        <Title level={5}>{t('siteAdmin.config.basic')}</Title>
                        <Form.Item name="slug" label={t('siteAdmin.config.slug')} rules={[{ required: true }]}>
                          <Input addonBefore="/s/" />
                        </Form.Item>
                        <Form.Item name="brand_name" label={t('siteAdmin.config.brandName')}>
                          <Input />
                        </Form.Item>
                        <Form.Item label={t('siteAdmin.config.logoUpload')}>
                          <Space align="start" size={16}>
                            {configForm.getFieldValue('logo_url') && (
                              <Image
                                src={configForm.getFieldValue('logo_url')}
                                alt="logo"
                                width={80}
                                height={80}
                                style={{ objectFit: 'contain', border: '1px solid #f0f0f0', borderRadius: 8, background: '#fafafa' }}
                                fallback="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAYAAAAfFcSJAAAADUlEQVR42mN8/5+hHgAHggJ/PchI7wAAAABJRU5ErkJggg=="
                              />
                            )}
                            <div>
                              <Upload
                                accept=".png,.jpg,.jpeg,.svg,.webp"
                                showUploadList={false}
                                customRequest={async ({ file, onSuccess, onError }) => {
                                  const url = await handleUploadFile(file, 'site');
                                  if (url) {
                                    configForm.setFieldsValue({ logo_url: url });
                                    onSuccess();
                                  } else {
                                    onError();
                                  }
                                }}
                              >
                                <Button icon={<UploadOutlined />}>{t('siteAdmin.config.logoUploadHint')}</Button>
                              </Upload>
                              <Form.Item name="logo_url" style={{ marginTop: 8, marginBottom: 0 }}>
                                <Input placeholder="https://..." size="small" />
                              </Form.Item>
                            </div>
                          </Space>
                        </Form.Item>
                        <Form.Item name="brand_color" label={t('siteAdmin.config.brandColor')}>
                          <Input type="color" style={{ width: 80 }} />
                        </Form.Item>
                        <Form.Item name="is_published" label={t('siteAdmin.config.publishStatus')} valuePropName="checked">
                          <Switch checkedChildren={t('siteAdmin.config.publishedOn')} unCheckedChildren={t('siteAdmin.config.publishedOff')} />
                        </Form.Item>
                        <Form.Item
                          name="alayaess_bot_name"
                          label="AlayaEss Agent（chatbot 綁定）"
                          tooltip="此情境網站上的 chatbot 會用這個 AlayaEss agent 的 persona。留空 = fallback Mode A（共用 Mika）。"
                          extra={<AlayaessAgentPickerHint />}
                        >
                          <AlayaessAgentPicker />
                        </Form.Item>
                      </Col>
                      <Col xs={24} md={12}>
                        <Title level={5}>{t('siteAdmin.config.hero')}</Title>
                        <Form.Item name="hero_title" label={t('siteAdmin.config.heroTitle')}>
                          <Input />
                        </Form.Item>
                        <Form.Item name="hero_subtitle" label={t('siteAdmin.config.heroSubtitle')}>
                          <TextArea rows={2} />
                        </Form.Item>
                        <Form.Item label={t('siteAdmin.config.heroImageUpload')}>
                          {configForm.getFieldValue('hero_image_url') && (
                            <div style={{ marginBottom: 8, borderRadius: 8, overflow: 'hidden', maxHeight: 120, border: '1px solid #f0f0f0' }}>
                              <img src={configForm.getFieldValue('hero_image_url')} alt="hero" style={{ width: '100%', display: 'block', objectFit: 'cover', maxHeight: 120 }} />
                            </div>
                          )}
                          <Upload
                            accept=".png,.jpg,.jpeg,.svg,.webp"
                            showUploadList={false}
                            customRequest={async ({ file, onSuccess, onError }) => {
                              const url = await handleUploadFile(file, 'site');
                              if (url) {
                                configForm.setFieldsValue({ hero_image_url: url });
                                onSuccess();
                              } else {
                                onError();
                              }
                            }}
                          >
                            <Button icon={<UploadOutlined />}>{t('siteAdmin.config.heroImageUploadHint')}</Button>
                          </Upload>
                          <Form.Item name="hero_image_url" style={{ marginTop: 8, marginBottom: 0 }}>
                            <Input placeholder="https://..." size="small" />
                          </Form.Item>
                        </Form.Item>
                        <Form.Item name="hero_cta_text" label={t('siteAdmin.config.heroCta')}>
                          <Input />
                        </Form.Item>
                      </Col>
                    </Row>
                    <Divider />
                    <Row gutter={24}>
                      <Col xs={24} md={12}>
                        <Title level={5}>{t('siteAdmin.config.contact')}</Title>
                        <Form.Item name="company_name" label={t('siteAdmin.config.companyName')}>
                          <Input />
                        </Form.Item>
                        <Form.Item name="contact_email" label={t('siteAdmin.config.contactEmail')}>
                          <Input />
                        </Form.Item>
                        <Form.Item name="contact_phone" label={t('siteAdmin.config.contactPhone')}>
                          <Input />
                        </Form.Item>
                        <Form.Item name="address" label={t('siteAdmin.config.address')}>
                          <TextArea rows={2} />
                        </Form.Item>
                      </Col>
                      <Col xs={24} md={12}>
                        <Title level={5}>{t('siteAdmin.config.seo')}</Title>
                        <Form.Item name="seo_title" label={t('siteAdmin.config.seoTitle')}>
                          <Input />
                        </Form.Item>
                        <Form.Item name="seo_description" label={t('siteAdmin.config.seoDesc')}>
                          <TextArea rows={3} />
                        </Form.Item>
                      </Col>
                    </Row>
                    <Form.Item>
                      <Button type="primary" htmlType="submit" loading={saving} size="large">
                        {t('siteAdmin.config.saveConfig')}
                      </Button>
                    </Form.Item>
                  </Form>
                </Card>
              ),
            },
            {
              key: 'navigation',
              label: <span><MenuOutlined /> {t('siteAdmin.config.navManagement')}</span>,
              children: (
                <Card>
                  <Title level={5} style={{ marginBottom: 4 }}>{t('siteAdmin.config.navManagement')}</Title>
                  <Paragraph type="secondary" style={{ marginBottom: 16 }}>
                    {t('siteAdmin.config.navDescription')}
                  </Paragraph>
                  <div style={{ marginBottom: 16 }}>
                    <Space direction="vertical" size={8}>
                      <Space align="center">
                        <Switch
                          checked={stickyCta}
                          onChange={setStickyCta}
                          checkedChildren={t('siteAdmin.config.publishedOn')}
                          unCheckedChildren={t('siteAdmin.config.publishedOff')}
                        />
                        <Text>{t('siteAdmin.config.navStickyCta')}</Text>
                      </Space>
                      <Space align="center">
                        <Switch
                          checked={showLogin}
                          onChange={setShowLogin}
                          checkedChildren={t('siteAdmin.config.publishedOn')}
                          unCheckedChildren={t('siteAdmin.config.publishedOff')}
                        />
                        <Text>{t('siteAdmin.config.navShowLogin')}</Text>
                        <Text type="secondary" style={{ fontSize: 12 }}>
                          {t('siteAdmin.config.navShowLoginHelp')}
                        </Text>
                      </Space>
                    </Space>
                  </div>
                  <div style={{ border: '1px solid #f0f0f0', borderRadius: 8, overflow: 'hidden' }}>
                    <div style={{ display: 'grid', gridTemplateColumns: '60px 1fr 1fr 1fr 70px', gap: 0, padding: '8px 12px', background: '#fafafa', fontWeight: 600, fontSize: 13, borderBottom: '1px solid #f0f0f0' }}>
                      <span></span>
                      <span>{t('siteAdmin.config.navLabel')}</span>
                      <span>{t('siteAdmin.config.navLabelTh')}</span>
                      <span></span>
                      <span style={{ textAlign: 'center' }}>{t('siteAdmin.config.navVisible')}</span>
                    </div>
                    {navTabs.map((tab, idx) => (
                      <div key={tab.key} style={{
                        display: 'grid', gridTemplateColumns: '60px 1fr 1fr 1fr 70px', gap: 8,
                        padding: '8px 12px', alignItems: 'center',
                        borderBottom: idx < navTabs.length - 1 ? '1px solid #f0f0f0' : 'none',
                        background: tab.visible ? '#fff' : '#fafafa',
                        opacity: tab.visible ? 1 : 0.6,
                      }}>
                        <Space size={2}>
                          <Button size="small" type="text" disabled={idx === 0} onClick={() => handleMoveNavTab(idx, -1)}>↑</Button>
                          <Button size="small" type="text" disabled={idx === navTabs.length - 1} onClick={() => handleMoveNavTab(idx, 1)}>↓</Button>
                        </Space>
                        <Input
                          size="small"
                          value={tab.label}
                          onChange={e => handleNavTabChange(idx, 'label', e.target.value)}
                          placeholder={t(`siteAdmin.config.navTab.${tab.key}`)}
                          addonBefore={<Text style={{ fontSize: 11, color: '#999' }}>🇹🇼</Text>}
                        />
                        <Input
                          size="small"
                          value={tab.label_th}
                          onChange={e => handleNavTabChange(idx, 'label_th', e.target.value)}
                          placeholder={t(`siteAdmin.config.navTab.${tab.key}`)}
                          addonBefore={<Text style={{ fontSize: 11, color: '#999' }}>🇹🇭</Text>}
                        />
                        <Tag color="blue" style={{ margin: 0, fontSize: 11 }}>{tab.key}</Tag>
                        <div style={{ textAlign: 'center' }}>
                          <Switch
                            size="small"
                            checked={tab.visible}
                            onChange={v => handleNavTabChange(idx, 'visible', v)}
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                  <div style={{ marginTop: 16 }}>
                    <Button type="primary" loading={saving} onClick={handleSaveNavConfig}>
                      {t('siteAdmin.config.saveConfig')}
                    </Button>
                  </div>
                </Card>
              ),
            },
            {
              key: 'sections',
              label: <span>📦 {t('siteAdmin.tabSections')} ({siteSections.length})</span>,
              children: (
                <Card>
                  <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8 }}>
                    <Title level={5} style={{ margin: 0 }}>{t('siteAdmin.sectionsMgmt.title')}</Title>
                    <Button type="primary" loading={autoGenLoading} onClick={handleAutoGenerate}>
                      🚀 {t('siteAdmin.sectionsMgmt.autoGenerate')}
                    </Button>
                  </div>
                  <Paragraph type="secondary" style={{ marginBottom: 16 }}>
                    {t('siteAdmin.sectionsMgmt.description')}
                  </Paragraph>
                  {siteSections.length === 0 ? (
                    <Empty description={t('siteAdmin.sectionsMgmt.noSections')} />
                  ) : (
                    <div>
                      {[...siteSections].sort((a, b) => a.section_order - b.section_order).map((sec, idx) => {
                        const now = new Date();
                        const isTimeLimited = sec.visible_from || sec.visible_until;
                        const isCurrentlyVisible = sec.is_visible &&
                          (!sec.visible_from || now >= new Date(sec.visible_from)) &&
                          (!sec.visible_until || now <= new Date(sec.visible_until));
                        const statusColor = !sec.is_visible ? '#d9d9d9' : isCurrentlyVisible ? '#52c41a' : '#faad14';
                        const statusText = !sec.is_visible ? t('siteAdmin.sectionsMgmt.statusDisabled') :
                          (sec.visible_from && now < new Date(sec.visible_from)) ? t('siteAdmin.sectionsMgmt.statusScheduled') :
                          (sec.visible_until && now > new Date(sec.visible_until)) ? t('siteAdmin.sectionsMgmt.statusExpired') : t('siteAdmin.sectionsMgmt.statusActive');
                        return (
                          <Card
                            key={sec.id}
                            size="small"
                            style={{ marginBottom: 8, borderLeft: `3px solid ${statusColor}` }}
                          >
                            <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8 }}>
                              <div style={{ flex: 1, minWidth: 200 }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                                  <Space size={4}>
                                    <Button size="small" disabled={idx === 0} onClick={() => handleMoveSection(sec.id, -1)}>↑</Button>
                                    <Button size="small" disabled={idx === siteSections.length - 1} onClick={() => handleMoveSection(sec.id, 1)}>↓</Button>
                                  </Space>
                                  <Tag color={statusColor === '#52c41a' ? 'green' : statusColor === '#faad14' ? 'orange' : 'default'}>{statusText}</Tag>
                                  <Text strong>{getSectionLabel(sec.section_type)}</Text>
                                  <Text type="secondary">— {sec.title || t('siteAdmin.sectionsMgmt.noTitle')}</Text>
                                  {sec.content_id && <Tag color="blue">AI #{sec.content_id}</Tag>}
                                </div>
                                {isTimeLimited && (
                                  <div style={{ fontSize: 12, color: '#999', marginLeft: 68 }}>
                                    {sec.visible_from && <span>{t('siteAdmin.sectionsMgmt.openTime')}{new Date(sec.visible_from).toLocaleString(i18n.language)}</span>}
                                    {sec.visible_from && sec.visible_until && <span> ~ </span>}
                                    {sec.visible_until && <span>{!sec.visible_from ? t('siteAdmin.sectionsMgmt.closeTime') : ''}{new Date(sec.visible_until).toLocaleString(i18n.language)}</span>}
                                  </div>
                                )}
                              </div>
                              <Space wrap>
                                <DatePicker
                                  size="small"
                                  placeholder={t('siteAdmin.sectionsMgmt.startDisplay')}
                                  showTime
                                  value={sec.visible_from ? dayjs(sec.visible_from) : null}
                                  onChange={(val) => handleUpdateSectionTime(sec.id, 'visible_from', val)}
                                  style={{ width: 170 }}
                                />
                                <DatePicker
                                  size="small"
                                  placeholder={t('siteAdmin.sectionsMgmt.stopDisplay')}
                                  showTime
                                  value={sec.visible_until ? dayjs(sec.visible_until) : null}
                                  onChange={(val) => handleUpdateSectionTime(sec.id, 'visible_until', val)}
                                  style={{ width: 170 }}
                                />
                                <Switch
                                  size="small"
                                  checked={sec.is_visible}
                                  onChange={() => handleToggleSectionVisible(sec)}
                                  checkedChildren={t('siteAdmin.sectionsMgmt.activeOn')}
                                  unCheckedChildren={t('siteAdmin.sectionsMgmt.activeOff')}
                                />
                                {sec.content_id && (
                                  <Button
                                    size="small"
                                    icon={<TranslationOutlined />}
                                    loading={translatingId === sec.content_id}
                                    onClick={() => handleTranslateContent(sec.content_id, 'th')}
                                  >
                                    {t('siteAdmin.sectionsMgmt.translateTh')}
                                  </Button>
                                )}
                                <Button size="small" icon={<EditOutlined />} onClick={() => handleOpenSectionEdit(sec)} />
                                <Popconfirm title={t('siteAdmin.sectionsMgmt.confirmDelete')} onConfirm={() => handleDeleteSection(sec.id)}>
                                  <Button size="small" type="text" danger icon={<DeleteOutlined />} />
                                </Popconfirm>
                              </Space>
                            </div>
                          </Card>
                        );
                      })}
                    </div>
                  )}

                  <Modal
                    title={editSection ? `${getSectionLabel(editSection.section_type)} — ${t('siteAdmin.sectionsMgmt.editContent')}` : ''}
                    open={!!editSection}
                    onCancel={() => { setEditSection(null); sectionForm.resetFields(); }}
                    onOk={() => sectionForm.submit()}
                    confirmLoading={saving}
                    okText={t('common.save')}
                    cancelText={t('common.cancel')}
                    width={640}
                    destroyOnClose
                  >
                    <Form form={sectionForm} layout="vertical" onFinish={handleSaveSectionEdit}>
                      <Tabs defaultActiveKey="zh-TW" items={[
                        {
                          key: 'zh-TW', label: <span>🇹🇼 {t('siteAdmin.langZh')}</span>,
                          children: (
                            <>
                              <Form.Item name="title" label={t('siteAdmin.sectionsMgmt.sectionTitle')}>
                                <Input placeholder={t('siteAdmin.sectionsMgmt.sectionTitlePlaceholder')} />
                              </Form.Item>
                              <Form.Item name="subtitle" label={t('siteAdmin.sectionsMgmt.sectionSubtitle')}>
                                <TextArea rows={2} placeholder={t('siteAdmin.sectionsMgmt.sectionSubtitlePlaceholder')} />
                              </Form.Item>
                            </>
                          ),
                        },
                        {
                          key: 'th', label: <span>🇹🇭 {t('siteAdmin.langTh')}</span>,
                          children: (
                            <>
                              <Form.Item name="title_th" label={t('siteAdmin.sectionsMgmt.sectionTitleTh')}>
                                <Input placeholder={t('siteAdmin.sectionsMgmt.sectionTitleThPlaceholder')} />
                              </Form.Item>
                              <Form.Item name="subtitle_th" label={t('siteAdmin.sectionsMgmt.sectionSubtitleTh')}>
                                <TextArea rows={2} placeholder={t('siteAdmin.sectionsMgmt.sectionSubtitleThPlaceholder')} />
                              </Form.Item>
                            </>
                          ),
                        },
                      ]} />
                    </Form>
                  </Modal>
                </Card>
              ),
            },
            {
              key: 'products',
              label: <span><ShoppingOutlined /> {t('siteAdmin.tabProducts')} ({siteProducts.length})</span>,
              children: (
                <Card>
                  <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8 }}>
                    <Title level={5} style={{ margin: 0 }}>{t('siteAdmin.productsMgmt.title')}</Title>
                    <Space>
                      <Button icon={<PlusOutlined />} type="primary" onClick={() => handleOpenProductModal('new')}>
                        {t('siteAdmin.productsMgmt.addNew')}
                      </Button>
                      {availableProducts.length > 0 && (
                        <Button onClick={() => setShowAddProduct(true)}>
                          {t('siteAdmin.productsMgmt.importExisting')}
                        </Button>
                      )}
                    </Space>
                  </div>
                  {siteProducts.length === 0 ? (
                    <Empty description={t('siteAdmin.productsMgmt.noProducts')} />
                  ) : (
                    <div>
                      {[...siteProducts].sort((a, b) => a.display_order - b.display_order).map((sp, idx) => (
                        <Card
                          key={sp.id}
                          size="small"
                          style={{ marginBottom: 8, borderLeft: sp.is_featured ? '3px solid #faad14' : '3px solid #e8e8e8' }}
                        >
                          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8 }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 10, flex: 1, minWidth: 0 }}>
                              <Space size={4}>
                                <Button size="small" disabled={idx === 0} onClick={() => handleMoveProduct(sp.id, -1)}>↑</Button>
                                <Button size="small" disabled={idx === siteProducts.length - 1} onClick={() => handleMoveProduct(sp.id, 1)}>↓</Button>
                              </Space>
                              {sp.product?.thumbnail ? (
                                <img src={sp.product.thumbnail} alt="" style={{ width: 44, height: 44, objectFit: 'cover', borderRadius: 6, flexShrink: 0 }} />
                              ) : (
                                <div style={{ width: 44, height: 44, borderRadius: 6, background: '#f0f0f0', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                                  <ShoppingOutlined style={{ color: '#bbb' }} />
                                </div>
                              )}
                              <div style={{ minWidth: 0 }}>
                                <div style={{ fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                  {sp.is_featured && <StarOutlined style={{ color: '#faad14', marginRight: 4 }} />}
                                  {sp.product?.name || `Product #${sp.product_id}`}
                                </div>
                                <div style={{ fontSize: 12, color: '#888' }}>
                                  {sp.product?.organization && <Tag color="blue" style={{ fontSize: 11 }}>{sp.product.organization}</Tag>}
                                  {sp.custom_price_note && <Text type="secondary">{sp.custom_price_note}</Text>}
                                  {sp.product?.short_description && !sp.custom_price_note && (
                                    <Text type="secondary" style={{ fontSize: 11 }}>{sp.product.short_description.substring(0, 40)}{sp.product.short_description.length > 40 ? '...' : ''}</Text>
                                  )}
                                </div>
                              </div>
                            </div>
                            <Space>
                              <Button size="small" icon={<StarOutlined style={{ color: sp.is_featured ? '#faad14' : '#ccc' }} />}
                                onClick={() => handleToggleFeatured(sp)} title={t('siteAdmin.productsMgmt.featuredProduct')} />
                              <Button size="small" icon={<EditOutlined />} onClick={() => handleOpenProductModal(sp)} title={t('common.edit')} />
                              <Popconfirm title={t('siteAdmin.productsMgmt.confirmDelete')} onConfirm={() => handleRemoveProduct(sp.id)}>
                                <Button size="small" type="text" danger icon={<DeleteOutlined />} />
                              </Popconfirm>
                            </Space>
                          </div>
                        </Card>
                      ))}
                    </div>
                  )}

                  {/* Import from existing products */}
                  <Modal
                    title={t('siteAdmin.productsMgmt.importTitle')}
                    open={showAddProduct}
                    onCancel={() => setShowAddProduct(false)}
                    footer={null}
                    width={600}
                  >
                    {availableProducts.length === 0 ? (
                      <Empty description={t('siteAdmin.productsMgmt.noImportable')} />
                    ) : (
                      <Table
                        dataSource={availableProducts}
                        rowKey="id"
                        pagination={{ pageSize: 10 }}
                        size="small"
                        columns={[
                          {
                            title: t('siteAdmin.productsMgmt.product'), dataIndex: 'name', render: (name, p) => (
                              <Space>
                                {p.thumbnail && <img src={p.thumbnail} alt="" style={{ width: 32, height: 32, objectFit: 'cover', borderRadius: 4 }} />}
                                <div>
                                  <div>{name}</div>
                                  {p.organization && <Text type="secondary" style={{ fontSize: 11 }}>{p.organization}</Text>}
                                </div>
                              </Space>
                            ),
                          },
                          {
                            title: '', width: 80, render: (_, p) => (
                              <Button size="small" type="primary" onClick={() => { handleAddProduct(p.id); setShowAddProduct(false); }}>
                                {t('siteAdmin.productsMgmt.import')}
                              </Button>
                            ),
                          },
                        ]}
                      />
                    )}
                  </Modal>

                  {/* Product Create / Edit Modal */}
                  <Modal
                    title={productModal === 'new' ? t('siteAdmin.productsMgmt.createTitle') : t('siteAdmin.productsMgmt.editTitle')}
                    open={!!productModal}
                    onCancel={() => { setProductModal(null); productForm.resetFields(); }}
                    onOk={() => productForm.submit()}
                    confirmLoading={saving}
                    okText={t('common.save')}
                    cancelText={t('common.cancel')}
                    width={680}
                    destroyOnClose
                  >
                    <Form form={productForm} layout="vertical" onFinish={handleSaveProduct}>
                      <Tabs defaultActiveKey="zh-TW" items={[
                        {
                          key: 'zh-TW', label: <span>🇹🇼 {t('siteAdmin.langZh')}</span>,
                          children: (
                            <>
                              <Row gutter={16}>
                                <Col span={16}>
                                  <Form.Item name="name" label={t('siteAdmin.productsMgmt.productName')} rules={[{ required: true, message: t('siteAdmin.productsMgmt.productNameRequired') }]}>
                                    <Input placeholder={t('siteAdmin.productsMgmt.productNamePlaceholder')} />
                                  </Form.Item>
                                </Col>
                                <Col span={8}>
                                  <Form.Item name="organization" label={t('siteAdmin.productsMgmt.orgBrand')}>
                                    <Input placeholder={t('siteAdmin.productsMgmt.orgPlaceholder')} />
                                  </Form.Item>
                                </Col>
                              </Row>
                              <Form.Item name="short_description" label={t('siteAdmin.productsMgmt.shortDesc')}>
                                <Input placeholder={t('siteAdmin.productsMgmt.shortDescPlaceholder')} />
                              </Form.Item>
                              <Form.Item name="description" label={t('siteAdmin.productsMgmt.fullDesc')}>
                                <TextArea rows={3} placeholder={t('siteAdmin.productsMgmt.fullDescPlaceholder')} />
                              </Form.Item>
                              <Form.Item name="custom_price_note" label={t('siteAdmin.productsMgmt.priceNote')}>
                                <Input placeholder={t('siteAdmin.productsMgmt.priceNotePlaceholder')} />
                              </Form.Item>
                              {productModal !== 'new' && (
                                <Form.Item name="custom_description" label={t('siteAdmin.productsMgmt.customDesc')}>
                                  <TextArea rows={2} placeholder={t('siteAdmin.productsMgmt.customDescPlaceholder')} />
                                </Form.Item>
                              )}
                            </>
                          ),
                        },
                        {
                          key: 'th', label: <span>🇹🇭 {t('siteAdmin.langTh')}</span>,
                          children: (
                            <>
                              <Form.Item name="name_th" label={t('siteAdmin.productsMgmt.productNameTh')}>
                                <Input placeholder={t('siteAdmin.productsMgmt.productNameThPlaceholder')} />
                              </Form.Item>
                              <Form.Item name="short_description_th" label={t('siteAdmin.productsMgmt.shortDescTh')}>
                                <Input placeholder={t('siteAdmin.productsMgmt.shortDescThPlaceholder')} />
                              </Form.Item>
                              <Form.Item name="description_th" label={t('siteAdmin.productsMgmt.fullDescTh')}>
                                <TextArea rows={3} placeholder={t('siteAdmin.productsMgmt.fullDescThPlaceholder')} />
                              </Form.Item>
                              <Form.Item name="custom_price_note_th" label={t('siteAdmin.productsMgmt.priceNoteTh')}>
                                <Input placeholder={t('siteAdmin.productsMgmt.priceNoteThPlaceholder')} />
                              </Form.Item>
                              {productModal !== 'new' && (
                                <Form.Item name="custom_description_th" label={t('siteAdmin.productsMgmt.customDescTh')}>
                                  <TextArea rows={2} placeholder={t('siteAdmin.productsMgmt.customDescThPlaceholder')} />
                                </Form.Item>
                              )}
                            </>
                          ),
                        },
                      ]} />
                      <Divider style={{ margin: '8px 0 16px' }} />
                      <Row gutter={16}>
                        <Col span={8}>
                          <Form.Item name="price" label={t('siteAdmin.productsMgmt.price')}>
                            <InputNumber style={{ width: '100%' }} placeholder="0" min={0} />
                          </Form.Item>
                        </Col>
                        <Col span={4}>
                          <Form.Item name="currency" label={t('siteAdmin.productsMgmt.currency')}>
                            <Input placeholder="TWD" />
                          </Form.Item>
                        </Col>
                        <Col span={12}>
                          <Form.Item name="thumbnail" label={t('siteAdmin.productsMgmt.thumbnail')}>
                            <Input placeholder={t('siteAdmin.productsMgmt.thumbnailPlaceholder')} />
                          </Form.Item>
                        </Col>
                      </Row>
                      <Form.Item name="features" label={t('siteAdmin.productsMgmt.features')}>
                        <TextArea rows={2} placeholder={"AI\nReport\nMulti-language"} />
                      </Form.Item>
                      <Form.Item name="tags" label={t('siteAdmin.productsMgmt.tags')}>
                        <Input placeholder={t('siteAdmin.productsMgmt.tagsPlaceholder')} />
                      </Form.Item>
                      <Form.Item name="is_featured" valuePropName="checked" label={t('siteAdmin.productsMgmt.featuredProduct')}>
                        <Switch />
                      </Form.Item>
                    </Form>
                  </Modal>
                </Card>
              ),
            },
            {
              key: 'events',
              label: <span><CalendarOutlined /> {t('siteAdmin.tabEvents')} ({events.length})</span>,
              children: (
                <Card>
                  <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between' }}>
                    <Title level={5} style={{ margin: 0 }}>{t('siteAdmin.eventsMgmt.title')}</Title>
                    <Button icon={<PlusOutlined />} type="primary" onClick={() => { setShowEventModal('new'); eventForm.resetFields(); }}>
                      {t('siteAdmin.eventsMgmt.addNew')}
                    </Button>
                  </div>
                  {events.length === 0 ? (
                    <Empty description={t('siteAdmin.eventsMgmt.noEvents')} />
                  ) : (
                    <div>
                      {events.map(evt => {
                        const status = getEventStatus(evt);
                        return (
                          <Card key={evt.id} size="small" style={{
                            marginBottom: 8,
                            borderLeft: `3px solid ${status.color === 'green' ? '#52c41a' : status.color === 'orange' ? '#faad14' : status.color === 'red' ? '#ff4d4f' : '#d9d9d9'}`,
                          }}>
                            <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8 }}>
                              <div style={{ flex: 1, minWidth: 200 }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                                  <Tag color={status.color}>{status.text}</Tag>
                                  <Text strong>{evt.title}</Text>
                                </div>
                                <Space size={16} wrap style={{ fontSize: 12, color: '#888' }}>
                                  {evt.event_date && (
                                    <span><CalendarOutlined style={{ marginRight: 4 }} />{new Date(evt.event_date).toLocaleString(i18n.language)}</span>
                                  )}
                                  {evt.location && <span>{evt.location}</span>}
                                  <span>{t('siteAdmin.eventsMgmt.registration')}<Button type="link" size="small" style={{ padding: 0 }} onClick={() => handleViewRegs(evt)}>{evt.registration_count || 0}</Button>{evt.max_attendees ? ` / ${evt.max_attendees}` : ''}</span>
                                </Space>
                                <div style={{ marginTop: 4, fontSize: 12, color: '#999' }}>
                                  {evt.visible_from && <span>{t('siteAdmin.eventsMgmt.openTime')}{new Date(evt.visible_from).toLocaleString(i18n.language)}</span>}
                                  {evt.visible_from && evt.visible_until && <span> ~ </span>}
                                  {evt.visible_until && <span>{!evt.visible_from ? t('siteAdmin.eventsMgmt.closeTime') : ''}{new Date(evt.visible_until).toLocaleString(i18n.language)}</span>}
                                  {evt.registration_deadline && <span style={{ marginLeft: 12, color: '#ff7a45' }}>{t('siteAdmin.eventsMgmt.regDeadline')}{new Date(evt.registration_deadline).toLocaleString(i18n.language)}</span>}
                                </div>
                              </div>
                              <Space>
                                <Switch size="small" checked={evt.is_active !== false} onChange={() => handleToggleEventActive(evt)}
                                  checkedChildren={t('siteAdmin.eventsMgmt.activeOn')} unCheckedChildren={t('siteAdmin.eventsMgmt.activeOff')} />
                                <Button size="small" icon={<EditOutlined />} onClick={() => {
                                  setShowEventModal(evt);
                                  eventForm.setFieldsValue({
                                    ...evt,
                                    event_date: evt.event_date ? dayjs(evt.event_date) : null,
                                    visible_from: evt.visible_from ? dayjs(evt.visible_from) : null,
                                    visible_until: evt.visible_until ? dayjs(evt.visible_until) : null,
                                    registration_deadline: evt.registration_deadline ? dayjs(evt.registration_deadline) : null,
                                  });
                                }} />
                                <Popconfirm title={t('siteAdmin.eventsMgmt.confirmDelete')} onConfirm={() => handleDeleteEvent(evt.id)}>
                                  <Button size="small" type="text" danger icon={<DeleteOutlined />} />
                                </Popconfirm>
                              </Space>
                            </div>
                          </Card>
                        );
                      })}
                    </div>
                  )}

                  <Modal
                    title={showEventModal === 'new' ? t('siteAdmin.eventsMgmt.createTitle') : t('siteAdmin.eventsMgmt.editTitle')}
                    open={!!showEventModal}
                    onCancel={() => { setShowEventModal(null); eventForm.resetFields(); }}
                    onOk={() => eventForm.submit()}
                    confirmLoading={saving}
                    okText={t('common.save')}
                    cancelText={t('common.cancel')}
                    width={640}
                  >
                    <Form form={eventForm} layout="vertical" onFinish={handleSaveEvent}>
                      <Tabs defaultActiveKey="zh-TW" items={[
                        {
                          key: 'zh-TW', label: <span>🇹🇼 {t('siteAdmin.langZh')}</span>,
                          children: (
                            <>
                              <Form.Item name="title" label={t('siteAdmin.eventsMgmt.eventNameLabel')} rules={[{ required: true, message: t('siteAdmin.eventsMgmt.eventNameRequired') }]}>
                                <Input placeholder={t('siteAdmin.eventsMgmt.eventNamePlaceholder')} />
                              </Form.Item>
                              <Form.Item name="description" label={t('siteAdmin.eventsMgmt.description')}>
                                <TextArea rows={3} placeholder={t('siteAdmin.eventsMgmt.descPlaceholder')} />
                              </Form.Item>
                              <Form.Item name="location" label={t('siteAdmin.eventsMgmt.locationLabel')}>
                                <Input placeholder={t('siteAdmin.eventsMgmt.locationPlaceholder')} />
                              </Form.Item>
                            </>
                          ),
                        },
                        {
                          key: 'th', label: <span>🇹🇭 {t('siteAdmin.langTh')}</span>,
                          children: (
                            <>
                              <Form.Item name="title_th" label={t('siteAdmin.eventsMgmt.eventNameThLabel')}>
                                <Input placeholder={t('siteAdmin.eventsMgmt.eventNameThPlaceholder')} />
                              </Form.Item>
                              <Form.Item name="description_th" label={t('siteAdmin.eventsMgmt.descriptionTh')}>
                                <TextArea rows={3} placeholder={t('siteAdmin.eventsMgmt.descThPlaceholder')} />
                              </Form.Item>
                              <Form.Item name="location_th" label={t('siteAdmin.eventsMgmt.locationThLabel')}>
                                <Input placeholder={t('siteAdmin.eventsMgmt.locationThPlaceholder')} />
                              </Form.Item>
                            </>
                          ),
                        },
                      ]} />
                      <Divider style={{ margin: '4px 0 12px' }} />
                      <Form.Item name="cover_image_url" label={t('siteAdmin.eventsMgmt.coverImage')}>
                        <Input placeholder="https://..." />
                      </Form.Item>
                      <Row gutter={16}>
                        <Col span={12}>
                          <Form.Item name="event_date" label={t('siteAdmin.eventsMgmt.eventDate')}>
                            <DatePicker showTime style={{ width: '100%' }} placeholder={t('siteAdmin.eventsMgmt.eventDatePlaceholder')} />
                          </Form.Item>
                        </Col>
                        <Col span={12}>
                          <Form.Item name="max_attendees" label={t('siteAdmin.eventsMgmt.maxAttendees')}>
                            <InputNumber min={1} style={{ width: '100%' }} placeholder={t('siteAdmin.eventsMgmt.maxAttendeesPlaceholder')} />
                          </Form.Item>
                        </Col>
                      </Row>
                      <Row gutter={16}>
                        <Col span={12}>
                          <Form.Item name="registration_deadline" label={t('siteAdmin.eventsMgmt.regDeadlineLabel')}>
                            <DatePicker showTime style={{ width: '100%' }} placeholder={t('siteAdmin.eventsMgmt.regDeadlinePlaceholder')} />
                          </Form.Item>
                        </Col>
                        <Col span={12}>
                          <Form.Item name="is_active" valuePropName="checked" label={t('siteAdmin.eventsMgmt.activeLabel')} initialValue={true}>
                            <Switch checkedChildren={t('siteAdmin.eventsMgmt.activeOn')} unCheckedChildren={t('siteAdmin.eventsMgmt.activeOff')} />
                          </Form.Item>
                        </Col>
                      </Row>
                      <Divider style={{ margin: '12px 0' }}>{t('siteAdmin.eventsMgmt.displayTimeDivider')}</Divider>
                      <Row gutter={16}>
                        <Col span={12}>
                          <Form.Item name="visible_from" label={t('siteAdmin.eventsMgmt.visibleFrom')}>
                            <DatePicker showTime style={{ width: '100%' }} placeholder={t('siteAdmin.eventsMgmt.visibleFromPlaceholder')} />
                          </Form.Item>
                        </Col>
                        <Col span={12}>
                          <Form.Item name="visible_until" label={t('siteAdmin.eventsMgmt.visibleUntil')}>
                            <DatePicker showTime style={{ width: '100%' }} placeholder={t('siteAdmin.eventsMgmt.visibleUntilPlaceholder')} />
                          </Form.Item>
                        </Col>
                      </Row>
                    </Form>
                  </Modal>

                  <Modal
                    title={regModal ? t('siteAdmin.eventsMgmt.regListTitle', { title: regModal.title }) : ''}
                    open={!!regModal}
                    onCancel={() => setRegModal(null)}
                    footer={null}
                    width={700}
                  >
                    <Table
                      dataSource={registrations}
                      rowKey="id"
                      pagination={false}
                      size="small"
                      columns={[
                        { title: t('siteAdmin.eventsMgmt.regName'), dataIndex: 'contact_name' },
                        { title: t('siteAdmin.eventsMgmt.regCompany'), dataIndex: 'company_name' },
                        { title: 'Email', dataIndex: 'email' },
                        { title: t('seminar.phone'), dataIndex: 'phone' },
                        { title: t('siteAdmin.eventsMgmt.regJobTitle'), dataIndex: 'job_title' },
                        {
                          title: t('siteAdmin.eventsMgmt.regTime'), dataIndex: 'created_at', width: 160,
                          render: d => d ? new Date(d).toLocaleString(i18n.language) : '-',
                        },
                      ]}
                    />
                  </Modal>
                </Card>
              ),
            },
            {
              key: 'inquiries',
              label: <span><PhoneOutlined /> {t('siteAdmin.tabInquiries')} ({inquiries.length})</span>,
              children: (
                <Card>
                  <Title level={5} style={{ marginBottom: 16 }}>{t('siteAdmin.inquiriesMgmt.title')}</Title>
                  {inquiries.length === 0 ? (
                    <Empty description={t('siteAdmin.inquiriesMgmt.noInquiries')} />
                  ) : (
                    <Table
                      dataSource={inquiries}
                      rowKey="id"
                      pagination={{ pageSize: 20 }}
                      columns={[
                        {
                          title: t('siteAdmin.inquiriesMgmt.time'), dataIndex: 'created_at', width: 160,
                          render: d => d ? new Date(d).toLocaleString(i18n.language) : '-',
                        },
                        { title: t('siteAdmin.inquiriesMgmt.name'), dataIndex: 'contact_name', width: 100 },
                        { title: t('siteAdmin.inquiriesMgmt.company'), dataIndex: 'company_name', width: 150 },
                        { title: 'Email', dataIndex: 'email', width: 180 },
                        { title: t('siteAdmin.inquiriesMgmt.phone'), dataIndex: 'phone', width: 120 },
                        {
                          title: t('siteAdmin.inquiriesMgmt.status'), dataIndex: 'status', width: 80,
                          render: s => (
                            <Tag color={s === 'new' ? 'blue' : s === 'contacted' ? 'green' : 'default'}>
                              {s === 'new' ? t('siteAdmin.inquiriesMgmt.statusNew') : s === 'contacted' ? t('siteAdmin.inquiriesMgmt.statusContacted') : s}
                            </Tag>
                          ),
                        },
                        {
                          title: t('siteAdmin.inquiriesMgmt.content'), dataIndex: 'message', ellipsis: true,
                        },
                      ]}
                    />
                  )}
                </Card>
              ),
            },
            {
              key: 'stats',
              label: t('siteAdmin.tabStats'),
              children: (
                <Row gutter={[16, 16]}>
                  <Col xs={12} sm={6}>
                    <Card><Statistic title={t('siteAdmin.stats.products')} value={siteProducts.length} prefix={<ShoppingOutlined />} /></Card>
                  </Col>
                  <Col xs={12} sm={6}>
                    <Card><Statistic title={t('siteAdmin.stats.events')} value={events.length} prefix={<CalendarOutlined />} /></Card>
                  </Col>
                  <Col xs={12} sm={6}>
                    <Card><Statistic title={t('siteAdmin.stats.inquiries')} value={inquiries.length} prefix={<PhoneOutlined />} /></Card>
                  </Col>
                  <Col xs={12} sm={6}>
                    <Card>
                      <Statistic
                        title={t('siteAdmin.stats.registrations')}
                        value={events.reduce((sum, e) => sum + (e.registration_count || 0), 0)}
                        prefix={<CalendarOutlined />}
                      />
                    </Card>
                  </Col>
                </Row>
              ),
            },
          ]}
        />
      )}
    </div>
  );
}
